import { ErrorBoundary } from "$components/error-boundary";
import { makeStyles } from "$components/mui-styles";
import { Table, type TableProps } from "$components/table";
import TablePagination from "$components/table/TablePagination";
import { linkToPatientDetails } from "$routes/patient/[:patientId]/links";
import { Box, styled, TableRow } from "@material-ui/core";
import React from "react";
import { useNavigate } from "react-router-dom";
import { PAGE_SIZE } from "./constants";
import {
  EmptyRegisterMessage,
  LoadingRegisterMessage,
} from "./empty-register-message";

const useStyles = makeStyles({
  cellWordBreak: {
    wordBreak: "break-word",
  },
});

const CenteredPaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginTop: theme.spacing(2),
}));

export const RegisterTable = ({
  currentPage,
  setCurrentPage,
  loading,
  rows,
  totalRows = 0,
  errorBoundaryName,
  columns,
  centerPagination = false,
}: {
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  loading: boolean;
  rows: TableProps["rows"];
  columns: TableProps["columns"];
  totalRows?: number;
  errorBoundaryName: string;
  centerPagination?: boolean;
}) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const table = (
    <Table
      hideHeader={false}
      stickyHeader
      columns={columns}
      rows={rows}
      noDataMessage={
        loading ? <LoadingRegisterMessage /> : <EmptyRegisterMessage />
      }
      onRowClick={(row) => {
        navigate(linkToPatientDetails({ patientUuid: row.patientUuid }));
      }}
      pagination={
        !centerPagination
          ? {
              page: currentPage,
              pageSize: PAGE_SIZE,
              totalRows,
              handlePageChange: (newPageNumber) => {
                setCurrentPage(newPageNumber);
              },
            }
          : undefined
      }
      classes={{ rowCellClass: () => classes.cellWordBreak }}
    />
  );

  // For tables with wide columns, we can center the pagination controls below the table
  return (
    <ErrorBoundary name={errorBoundaryName}>
      {centerPagination && totalRows > PAGE_SIZE ? (
        <>
          {table}
          <CenteredPaginationContainer>
            <TableRow>
              <TablePagination
                columns={columns}
                rows={rows}
                numRows={totalRows}
                pagination={{
                  page: currentPage,
                  pageSize: PAGE_SIZE,
                  totalRows,
                  handlePageChange: (newPageNumber) => {
                    setCurrentPage(newPageNumber);
                  },
                }}
                editedRow={{ index: null, id: "pagination-row" }}
                setEditedRow={() => {}}
              />
            </TableRow>
          </CenteredPaginationContainer>
        </>
      ) : (
        table
      )}
    </ErrorBoundary>
  );
};
